import React, { useState } from 'react'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { AlertDashboardWidget } from '@/components/alerts/AlertDashboardWidget'
import { AlertConfigPanel } from '@/components/alerts/AlertConfigPanel'
import { AlertHistory } from '@/components/alerts/AlertHistory'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Bell, Settings, History, TestTube } from 'lucide-react'
import { useAlerts } from '@/contexts/AlertContext'

export function AlertManagement() {
  const { activeAlerts, addAlert } = useAlerts()
  const [activeTab, setActiveTab] = useState('overview')

  const criticalCount = activeAlerts.filter(a => a.severity === 'critical').length
  const highCount = activeAlerts.filter(a => a.severity === 'high').length

  const generateTestAlert = () => {
    const testAlerts = [
      {
        severity: 'critical' as const,
        type: 'sequential' as const,
        title: 'Test: Sequential Phone Attack',
        message: 'This is a test critical alert for sequential phone pattern detection',
        affectedLeads: 5,
        actions: [
          {
            id: 'block',
            label: 'Block Pattern',
            type: 'destructive' as const,
            handler: () => console.log('Block pattern clicked')
          },
          {
            id: 'review',
            label: 'Review Leads',
            type: 'secondary' as const,
            handler: () => console.log('Review leads clicked')
          }
        ]
      },
      {
        severity: 'high' as const,
        type: 'velocity' as const,
        title: 'Test: Velocity Attack',
        message: 'This is a test high priority alert for velocity attack detection',
        affectedLeads: 3
      },
      {
        severity: 'medium' as const,
        type: 'datacenter' as const,
        title: 'Test: Data Center IP',
        message: 'This is a test medium priority alert for data center IP detection',
        affectedLeads: 1
      }
    ]

    const randomAlert = testAlerts[Math.floor(Math.random() * testAlerts.length)]
    addAlert(randomAlert)
  }

  return (
    <div className="w-full max-w-7xl mx-auto p-6">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <Bell className="h-6 w-6 text-foreground" />
          <h1 className="text-2xl font-bold text-foreground">
            Alert Management
          </h1>
          {activeAlerts.length > 0 && (
            <div className="flex items-center gap-2">
              {criticalCount > 0 && (
                <Badge className="bg-risk-critical text-white animate-pulse">
                  {criticalCount} Critical
                </Badge>
              )}
              {highCount > 0 && (
                <Badge className="bg-risk-high text-white">
                  {highCount} High
                </Badge>
              )}
            </div>
          )}
        </div>
        
        <Button
          onClick={generateTestAlert}
          variant="outline"
          className="gap-2"
        >
          <TestTube className="h-4 w-4" />
          Generate Test Alert
        </Button>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className="grid w-full grid-cols-3 lg:w-fit">
          <TabsTrigger value="overview" className="gap-2">
            <Bell className="h-4 w-4" />
            Overview
            {activeAlerts.length > 0 && (
              <Badge variant="secondary" className="ml-1 px-1 py-0 text-xs min-w-5 h-5">
                {activeAlerts.length}
              </Badge>
            )}
          </TabsTrigger>
          <TabsTrigger value="config" className="gap-2">
            <Settings className="h-4 w-4" />
            Configuration
          </TabsTrigger>
          <TabsTrigger value="history" className="gap-2">
            <History className="h-4 w-4" />
            History
          </TabsTrigger>
        </TabsList>

        <TabsContent value="overview" className="mt-6">
          <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
            <AlertDashboardWidget 
              showHistory={false}
              maxAlerts={10}
            />
            <AlertDashboardWidget 
              showHistory={true}
              maxAlerts={5}
              className="xl:col-span-1"
            />
          </div>
        </TabsContent>

        <TabsContent value="config" className="mt-6">
          <AlertConfigPanel />
        </TabsContent>

        <TabsContent value="history" className="mt-6">
          <AlertHistory />
        </TabsContent>
      </Tabs>
    </div>
  )
}